import { InputValidationGroup, InputValidations } from './types';

import { updateNextButton } from '@/utils/tour_guide.util';
import { validateLogicalGroup } from './logic';

// Store input listeners for cleanup
const inputListeners = new Map<string, (event: Event) => void>();

interface DropdownConfig {
  containerSelector: string;
  tagsSelector?: string;      // Optional selectors for different dropdown implementations
  optionsSelector?: string;
  menuSelector?: string;
}

const getDropdownSelectors = (element: Element): DropdownConfig => {
  // Check for Naive UI structure
  if (element.classList.contains('n-select')) {
    return {
      containerSelector: '.n-base-selection',
      tagsSelector: '.n-base-selection-tags',
      optionsSelector: '.n-select-option',
      menuSelector: '.n-select-menu'
    };
  }

  // Add more dropdown library checks here
  // Example for a different dropdown library:
  // if (element.classList.contains('other-dropdown')) {
  //   return different selectors
  // }

  // Default selectors
  return {
    containerSelector: '*',
    tagsSelector: '[role="listbox"]',
    optionsSelector: '[role="option"]',
    menuSelector: '[role="listbox"]'
  };
};

// Set up input listeners
const setupFieldValidation = (selector: string, inputValidations: InputValidations) => {
  // console.log('::setupFieldValidation::', selector);

  const element = document.querySelector(selector);
  if (!element) return;

  const dropdownConfig = getDropdownSelectors(element);
  const uniqueId = `${selector}-${Math.random().toString(36).substr(2, 9)}`;

  const inputListener = () => {
    if (!inputValidations) return;
    // console.log('::inputValidations::', inputValidations);
    const isValid = validateLogicalGroup(
      inputValidations.logic,
      inputValidations.fields
    );
    // console.log('::isValid::', isValid);
    updateNextButton({ isValid });
  };

  // Store listener reference with unique ID
  inputListeners.set(uniqueId, inputListener);


  // Add direct event listeners
  const events = ['input', 'click', 'change', 'blur'];
  events.forEach(eventType => {
    element.addEventListener(eventType, inputListener);
    // Store event cleanup information
    inputListeners.set(`${uniqueId}-${eventType}`, () => {
      element.removeEventListener(eventType, inputListener);
    });
  });

  // Watch for changes in the container
  const containerObserver = new MutationObserver(inputListener);
  containerObserver.observe(element, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true
  });

  // Watch for dropdown menu being added to body
  const bodyObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement &&
          node.matches(dropdownConfig.menuSelector || '')) {
          // Find the closest trigger element to identify which dropdown this belongs to
          const options = node.querySelectorAll(dropdownConfig.optionsSelector || '');
          options.forEach(option => {
            option.addEventListener('click', inputListener);
          });
        }
      });
    });
  });

  bodyObserver.observe(document.body, {
    childList: true,
    subtree: false
  });

  // Store observers for cleanup with unique IDs
  inputListeners.set(`${uniqueId}-container-observer`, () => containerObserver.disconnect());
  inputListeners.set(`${uniqueId}-body-observer`, () => bodyObserver.disconnect());
}

// Cleanup function for input validations
export const cleanupInputValidations = (inputValidations: InputValidations) => {
  if (!inputValidations) return;

  // console.log('Starting cleanup of input validations');
  const allSelectors = new Set<string>();

  const addFieldsFromLogic = (group: InputValidationGroup) => {
    if (Array.isArray(group.fields)) {
      group.fields.forEach((field: InputValidationGroup | string) => {
        if (typeof field === 'string') {
          allSelectors.add(field);
        } else {
          addFieldsFromLogic(field);
        }
      });
    }
  };

  addFieldsFromLogic(inputValidations.logic);
  // console.log('Fields to clean:', Array.from(allSelectors));

  // Remove listeners from each field
  allSelectors.forEach(selector => {
    // Find all related listeners by checking if the key starts with the selector
    Array.from(inputListeners.keys())
      .filter(key => key.startsWith(selector))
      .forEach(key => {
        const cleanup = inputListeners.get(key);
        if (cleanup) {
          cleanup(new Event('cleanup'));
          inputListeners.delete(key);
        }
      });
  });

  // Double-check that all listeners are removed
  if (inputListeners.size > 0) {
    console.warn('Some listeners remained after cleanup:', Array.from(inputListeners.keys()));
  } else {
    // console.log('All listeners successfully removed');
  }
};

// Initialize validations
export const setupAllValidations = (inputValidations: InputValidations) => {
  // Clean up existing validations first
  cleanupInputValidations(inputValidations)

  const allSelectors = new Set<string>()

  const addFieldsFromLogic = (group: InputValidationGroup) => {
    if (Array.isArray(group.fields)) {
      group.fields.forEach((field: InputValidationGroup | string) => {
        if (typeof field === 'string') {
          allSelectors.add(field)
        } else {
          addFieldsFromLogic(field)
        }
      })
    }
  }

  // console.log('::adding fields from logic::')
  addFieldsFromLogic(inputValidations.logic)
  // console.log('::adding fields from logic done::')
  // console.log('::allSelectors::', allSelectors)
  allSelectors.forEach(selector => setupFieldValidation(selector, inputValidations))
}